<template>
	<div ref="qrPanel" class="qr-panel">
		<div class="row">
			<client-only>
				<div class="columns">
					<div class="qr-container booking bookForm">
						<div class="qr-section">
							<date-picker
								v-model:value="state.arrival"
								:clearable="false"
								:disabled-date="disableArrivalDates"
								value-type="format"
								format="YYYY-MM-DD"
								:placeholder="$t('formArrival')"
								class="border-right"
								:lang="datepickerLang"
								@pick="pickArrival"
							>
								<template #icon-calendar> &nbsp; </template>
							</date-picker>
						</div>
						<div class="qr-section">
							<date-picker
								v-model:value="state.departure"
								:open="state.openDeparture"
								:disabled-date="disableDepartureDates"
								value-type="format"
								format="YYYY-MM-DD"
								:placeholder="$t('formDeparture')"
								:default-value="state.pickedArrivalDate ?? new Date()"
								class=""
								:lang="datepickerLang"
								@open="state.openDeparture = true"
								@close="state.openDeparture = false"
							>
								<template #icon-calendar> &nbsp; </template>
							</date-picker>
						</div>
						<book-button class="qr-section button" :arrival="state.arrival" :departure="state.departure">
							{{ $t('checkAvailability') }}
						</book-button>
					</div>
				</div>
			</client-only>
		</div>
	</div>
</template>

<script setup>
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import datePickerTranslations from '~/i18n/datePicker.ts';

const { locale } = useI18n();

defineProps({
	bookingForm: { type: Object, default: () => ({}) },
});

const state = reactive({
	arrival: null,
	departure: null,
	openDeparture: false,
	pickedArrivalDate: null,
});

const qrPanel = ref(null);

const datepickerLang = computed(() => datePickerTranslations[locale] ?? datePickerTranslations.en);

const pickArrival = (date) => {
	state.departure = '';
	state.pickedArrivalDate = date;
	state.openDeparture = true;
};

const disableArrivalDates = (date) => {
	const today = new Date();

	return date < today.setDate(today.getDate() - 1);
};

const disableDepartureDates = (date) => {
	if (!state.pickedArrivalDate) {
		return disableArrivalDates(date);
	}

	return date <= state.pickedArrivalDate;
};

onMounted(() => {
	qrPanel.value.classList.add('mounted');
});
</script>

<style lang="scss">
.mx-calendar-content .cell.active {
	background-color: var(--primary-color);
}
</style>

<style lang="scss" scoped>
.qr-panel {
	position: absolute;
	inset: auto 0 50px;
	z-index: 2;
	margin: 0 auto -200px;
	opacity: 0;
	transition: all 0.8s ease-out;
	animation: fade-from-bottom 0.9s ease;

	&.mounted {
		margin: 0 auto;
		opacity: 1;
	}

	.columns:first-child {
		padding: 0;
		margin: 0 auto;
		float: none;
		text-align: center;
	}
}

.qr-container {
	display: flex;
	margin: 0 auto;
	width: 90%;
	max-width: 860px;
}

.qr-section {
	float: left;
	padding: 22px 0;
	position: relative;
	width: 32%;

	label {
		text-transform: uppercase;
		color: var(--primary-background-color);
		float: left;
		position: absolute;
		top: 25px;
		left: 20px;
		right: 0;
		font-size: 13px;
		letter-spacing: 2px;
	}

	:deep(input:not(.button)),
	:deep(select) {
		appearance: none;
		border: none;
		box-shadow: none;
		font-size: 18px;
		font-family: var(--body-font-family);
		font-weight: 300;
		background: url('~/assets/images/calendar-icon.png') no-repeat center right 40px;
		background-size: 13px;
		position: relative;
		z-index: 1;
		cursor: pointer;
		padding: 0 40px;
		color: var(--primary-text-color);
		height: 52px;

		&::placeholder {
			color: var(--primary-text-color);
		}

		&:focus {
			outline: #fff;
		}
	}

	.border-right {
		border-right: 1px solid var(--primary-text-color);
	}

	&:not(.button) {
		background: var(--primary-background-color);
	}

	&.button {
		padding: 30px 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 36%;
		text-align: center;
		color: #fff;
		border: none;
	}
}

.qr-panel .right a {
	margin: 40px 0 0;
	float: right;
}

@media (max-width: 1280px) {
	.qr-section {
		padding: 20px 10px;
		max-width: 260px;

		label {
			left: 10px;
		}
	}
}

@media (max-width: 1080px) {
	.qr-section {
		padding: 20px 8px;
		max-width: 225px;
	}
}

@media (max-width: 960px) {
	.qr-panel {
		display: none;
	}
}
</style>
